/**
 * Authentication API
 * RTK Query endpoints for user authentication and profile management
 */

import { skipToken } from "@reduxjs/toolkit/query";
import { tokenStorage } from "../../config/api";
import type { LoginCredentials, User } from "../../types";
import { performLogoutCleanup } from "../../utils/localStorageManager";
import { baseApi } from "./baseApi";

/**
 * Registration data interface
 */
export interface RegisterData {
	email: string;
	password: string;
	firstName: string;
	lastName: string;
	phone?: string;
	agreeToTerms: boolean;
	role?: "tenant" | "agent" | "technician"; // Optional, defaults to tenant on backend
}

/**
 * Login response interface
 */
export interface LoginResponse {
	access: string;
	refresh: string;
	user: User;
}

/**
 * Register response interface
 */
export interface RegisterResponse {
	user: User;
	message: string;
}

/**
 * Password change interface
 */
export interface PasswordChangeData {
	old_password: string;
	new_password: string;
}

/**
 * Password reset request interface
 */
export interface PasswordResetRequestData {
	email: string;
}

/**
 * Password reset confirm interface
 */
export interface PasswordResetConfirmData {
	token: string;
	password: string;
}

/**
 * Email validation response interface
 */
export interface EmailValidationResponse {
	valid: boolean;
	message: string;
}

/**
 * Authentication API endpoints
 */
export const authApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * Login user
		 */
		login: builder.mutation<LoginResponse, LoginCredentials>({
			query: (credentials) => ({
				url: "/auth/login",
				method: "POST",
				body: credentials,
			}),
			async onQueryStarted(_args, { queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					// Store tokens in localStorage
					tokenStorage.setTokens(data.access, data.refresh);
				} catch (error) {
					// Handle error
					console.error("Login failed:", error);
				}
			},
			invalidatesTags: ["Auth"],
		}),

		/**
		 * Register new user
		 */
		register: builder.mutation<RegisterResponse, RegisterData>({
			query: (data) => {
				// Remove agreeToTerms as FastAPI doesn't expect it
				const { agreeToTerms, ...requestData } = data;
				return {
					url: "/auth/register",
					method: "POST",
					body: requestData,
				};
			},
			invalidatesTags: ["Auth"],
		}),

		/**
		 * Get current user profile
		 * Requires authentication
		 *
		 * Usage with skipToken for public pages:
		 * const { data: user } = useGetProfileQuery(accessToken ? undefined : skipToken);
		 */
		getProfile: builder.query<User, void | typeof skipToken>({
			query: () => ({
				url: "/auth/profile",
				method: "GET",
			}),
			transformResponse: (response: any): User => {
				// Ensure role is always a string matching UserRole type
				return {
					...response,
					role: String(response.role || "tenant") as User["role"],
				};
			},
			providesTags: ["User", "Auth"],
		}),

		/**
		 * Update current user profile
		 * Requires authentication
		 */
		updateProfile: builder.mutation<
			User,
			{
				firstName: string;
				lastName: string;
				phone?: string;
				email_notifications?: boolean;
				sms_notifications?: boolean;
				// Tenant fields
				emergency_contact_name?: string;
				emergency_contact_phone?: string;
				emergency_contact_relation?: string;
				// Technician fields
				primary_specialty?: string;
				experience?: string;
				license_number?: string;
				// Agent fields
				brokerage_name?: string;
				service_areas?: string;
				// Admin fields
				company_name?: string;
				job_title?: string;
			}
		>({
			query: (data) => ({
				url: "/auth/profile",
				method: "PATCH",
				body: {
					first_name: data.firstName,
					last_name: data.lastName,
					phone: data.phone || "",
					email_notifications: data.email_notifications,
					sms_notifications: data.sms_notifications,
					// Tenant fields
					emergency_contact_name: data.emergency_contact_name,
					emergency_contact_phone: data.emergency_contact_phone,
					emergency_contact_relation: data.emergency_contact_relation,
					// Technician fields
					primary_specialty: data.primary_specialty,
					experience: data.experience,
					license_number: data.license_number,
					// Agent fields
					brokerage_name: data.brokerage_name,
					service_areas: data.service_areas,
					// Admin fields
					company_name: data.company_name,
					job_title: data.job_title,
				},
			}),
			invalidatesTags: ["User", "Auth"],
		}),

		/**
		 * Change password
		 * Requires authentication
		 */
		changePassword: builder.mutation<{ message: string }, PasswordChangeData>({
			query: (data) => ({
				url: "/auth/password/change",
				method: "POST",
				body: {
					old_password: data.old_password,
					new_password: data.new_password,
					confirm_password: data.new_password, // FastAPI expects confirm_password
				},
			}),
		}),

		/**
		 * Request password reset
		 * Sends reset email to user
		 */
		requestPasswordReset: builder.mutation<
			{ message: string },
			PasswordResetRequestData
		>({
			query: (data) => ({
				url: "/auth/password/reset/request",
				method: "POST",
				body: data,
			}),
		}),

		/**
		 * Confirm password reset
		 * Resets password using token from email
		 */
		confirmPasswordReset: builder.mutation<
			{ message: string },
			PasswordResetConfirmData
		>({
			query: (data) => ({
				url: "/auth/password/reset/confirm",
				method: "POST",
				body: {
					token: data.token,
					new_password: data.password, // Backend expects new_password (not password)
					confirm_password: data.password, // Backend requires confirm_password
				},
			}),
		}),

		/**
		 * Validate email address
		 * Used for real-time validation during registration
		 */
		validateEmail: builder.mutation<EmailValidationResponse, { email: string }>(
			{
				query: (data) => ({
					url: "/auth/validate-email",
					method: "POST",
					body: { email: data.email },
				}),
			},
		),

		/**
		 * Logout user
		 * Clears tokens from storage and resets RTK Query cache
		 * Uses comprehensive cleanup to handle corrupted localStorage
		 */
		logout: builder.mutation<void, void>({
			queryFn: async () => {
				// Perform comprehensive logout cleanup
				performLogoutCleanup();
				return { data: undefined };
			},
			invalidatesTags: ["Auth", "User"],
			onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled;
					// Reset the entire RTK Query cache to clear all cached data
					dispatch(baseApi.util.resetApiState());
				} catch (error) {
					console.error("Logout error:", error);
					// Even if there's an error, try to cleanup
					try {
						performLogoutCleanup();
						dispatch(baseApi.util.resetApiState());
					} catch (cleanupError) {
						console.error("Cleanup after logout error failed:", cleanupError);
					}
				}
			},
		}),
	}),
});

/**
 * Export hooks for usage in functional components
 */
export const {
	useLoginMutation,
	useRegisterMutation,
	useGetProfileQuery,
	useLazyGetProfileQuery,
	useUpdateProfileMutation,
	useChangePasswordMutation,
	useRequestPasswordResetMutation,
	useConfirmPasswordResetMutation,
	useValidateEmailMutation,
	useLogoutMutation,
} = authApi;
